import { Events, EventsGlobal } from '@shared/Events';
import { FreeCrossSectionTool } from '@openlayers/tools/tool-free-crosssection';
import { FreeLongSectionTool } from '@openlayers/tools/tool-free-longsection';
import { DrawInteractionTool } from '@openlayers/tools/tool-base';
import { DistanceTool } from '@openlayers/tools/tool-distance';
import { AreaTool } from '@openlayers/tools/tool-area';
import { VolumeTool } from '@openlayers/tools/volume/tool-volumes';
import { setMapCursor } from '@openlayers/ol-utils';
import * as olExtent from 'ol/extent';
import { autorun, makeObservable, observable } from 'mobx';
import { MapWrapper } from '@openlayers/MapWrapper';
import { transform as olTransform } from 'ol/proj';
import Feature from 'ol/Feature';
import { MapLocationPickerTool } from '@openlayers/tools/tool-location-picker';
import { MeasureToolInteractions } from '@openlayers/tools/tool-interactions';
import { Drawer } from '@shared/ui-native/Drawer';
import { MaterialsStore } from '@pages/trucks/store/MaterialsStore';
import { Polygon } from 'ol/geom';
import { GeofenceEditor } from '@pages/trucks/geofences/GeofenceEditor';
import { GeofencesStore } from '@pages/trucks/store/GeofencesStore';
import { MapCoordsService } from '@openlayers/MapCoordsService';
import { MapStationEvents } from '@openlayers/MapStationLayer';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { openVolumeWindow, closeWindow } from '@openlayers/tools/volume/open-dialog';
import { IFloatingWindowStoredState, WindowManager } from '@shared/ui-react/FloatingWindow';
import * as React from 'react';
import { LStorage } from '@shared/Storage';
import { CrossSectionComponent } from '@pages/cross-section/cross-section/CrossSectionComponent';
import { LongSectionComponent } from '@pages/cross-section/long-section/LongSectionComponent';
import { ICrossSectionLoadSelection, ICrossSectionResponse } from '@pages/cross-section/cross-section/CrossSectionStore';
import { CoordsUtils } from '@shared/utils/CoordsUtils';
import { v4 as uuidv4 } from 'uuid';
import { Ajax2 } from '@shared/Ajax';
import { Telemetry, TelemetryEventType, TelemetrySource } from '@shared/Telemetry';
import { SimpleMeasurementsStore } from '@openlayers/map-measurements/SimpleMeasurementsStore';
import { TreeNode } from '@shared/ui-native/Tree';

export class MapToolsStore {
    public mapWrapper: MapWrapper;
    simpleMeasurementsStore: SimpleMeasurementsStore = null;

    public getSelectedModels: () => number[];
    public getSelectedModelNodes: () => TreeNode[];
    public getSelectedLumiDbUuids: () => string[];
    public getSelectedDemUuids: () => string[];

    @observable.ref activeTool: any | DrawInteractionTool;
    distanceTool: DistanceTool;
    areaTool: AreaTool;
    toolInteractions: MeasureToolInteractions;

    freeCrossSectionTool: FreeCrossSectionTool;
    freeLongSectionTool: FreeLongSectionTool;
    locationPickerTool: MapLocationPickerTool;
    volumeTool: VolumeTool;
    volumeToolType: string;

    @observable hasMapAlignment = false;
    @observable hasMapStation = false;
    @observable hasMeasurements = false;
    @observable openCrossSectionInNewTab = window.innerWidth < 800 ? false : LStorage.getOrDefault('openCrossSectionInNewTab', false);
    @observable showSegmentLabels: boolean = LStorage.getOrDefault('mapMeasurement-showSegmentLabels', true);

    init(mapWrapper: MapWrapper, getSelectedModels: () => number[], getSelectedModelNodes: () => TreeNode[], getSelectedPointCloudsUuids: () => string[], getSelectedDemUuids: () => string[]) {
        Events.on('mapStationRemoved', () => (this.hasMapStation = false));
        Events.on('mapStationAdded', () => (this.hasMapStation = true));
        Events.on('mapActiveAlignmentRemoved', () => (this.hasMapAlignment = false));
        Events.on('mapActiveAlignmentAdded', () => (this.hasMapAlignment = true));

        this.mapWrapper = mapWrapper;
        this.simpleMeasurementsStore = new SimpleMeasurementsStore();
        this.getSelectedModels = getSelectedModels;
        this.getSelectedModelNodes = getSelectedModelNodes;
        this.getSelectedLumiDbUuids = getSelectedPointCloudsUuids;
        this.getSelectedDemUuids = getSelectedDemUuids;

        this.areaTool = new AreaTool(mapWrapper);
        this.distanceTool = new DistanceTool(mapWrapper);
        this.volumeTool = new VolumeTool(mapWrapper);
        this.volumeToolType = '';
        this.toolInteractions = new MeasureToolInteractions(mapWrapper, [this.areaTool.drawLayer.getSource(), this.distanceTool.drawLayer.getSource()]);

        this.areaTool.onDrawEndCallback = this.toolInteractions.addFeature;
        this.areaTool.onDrawRemove = this.toolInteractions.removeFeature;
        this.areaTool.showSegmentLabels = this.showSegmentLabels;
        this.areaTool.onDrawProgressCallback = feature => this.toolInteractions.addGonsSnap(feature);

        this.volumeTool.onDrawEndCallback = this.toolInteractions.addFeature;
        this.volumeTool.onDrawRemove = this.toolInteractions.removeFeature;
        this.volumeTool.onDrawProgressCallback = feature => this.toolInteractions.addGonsSnap(feature);

        this.distanceTool.onDrawEndCallback = this.toolInteractions.addFeature;
        this.distanceTool.onDrawRemove = this.toolInteractions.removeFeature;
        this.distanceTool.onDrawProgressCallback = feature => this.toolInteractions.addGonsSnap(feature);
        this.distanceTool.showSegmentLabels = this.showSegmentLabels;

        this.freeLongSectionTool = new FreeLongSectionTool(mapWrapper, this.openLongSectionWindow);
        this.freeCrossSectionTool = new FreeCrossSectionTool(mapWrapper, (latLon1, latLon2) => this.openCrossSectionWindow(null, latLon1, latLon2, null));

        this.distanceTool.drawLayer.getSource().on('addfeature', this.onMeasurementsChange);
        this.distanceTool.drawLayer.getSource().on('removefeature', this.onMeasurementsChange);
        this.areaTool.drawLayer.getSource().on('addfeature', this.onMeasurementsChange);
        this.areaTool.drawLayer.getSource().on('removefeature', this.onMeasurementsChange);
        this.volumeTool.drawLayer.getSource().on('addfeature', this.onMeasurementsChange);
        this.volumeTool.drawLayer.getSource().on('removefeature', this.onMeasurementsChange);
    }

    constructor() {
        makeObservable(this);
        autorun(() => {
            LStorage.setItem('openCrossSectionInNewTab', this.openCrossSectionInNewTab);
        });
    }

    activateTool = (tool: MapTool) => {
        if (this.activeTool && this.activeTool.deactivate) {
            this.activeTool.deactivate();
        }
        setMapCursor(this.mapWrapper.getInstance(), '');
        if (tool) {
            setMapCursor(this.mapWrapper.getInstance(), 'crosshair');
            tool.activate();
            this.toolInteractions.activate();
        } else {
            this.toolInteractions.deactivate();
        }
        this.activeTool = tool;

        // gui
        Events.trigger('map-hide-overlays');
    };

    isAnyToolActive = () => {
        return this.activeTool !== null;
    };

    deactivateAllTools = () => {
        closeWindow();
        this.activateTool(null);
    };

    activateAreaTool = () => {
        this.activateTool(this.areaTool);
    };

    activateDistanceTool = () => {
        this.activateTool(this.distanceTool);
    };

    activateLongCsTool = () => {
        this.activateTool(this.freeLongSectionTool);
        this.freeLongSectionTool.startDrawing();
    };

    activateFreeCsTool = () => {
        this.activateTool(this.freeCrossSectionTool);
        Telemetry.add(TelemetryEventType.MAP_2D_TOOLBAR_FREE_CS, TelemetrySource.MAP_2D_DESKTOP);
    };

    activateLocationPickerTool = (origin: { lat: number; lon: number }, onConfirmed, onCancel?, confirmLabel?: string) => {
        this.locationPickerTool = new MapLocationPickerTool(this.mapWrapper, origin, this.deactivateAllTools, onConfirmed, onCancel, confirmLabel);
        this.activateTool(this.locationPickerTool);
    };

    activateStockpileVolumeTool = () => {
        console.log('activateStockpileVolumeTool');
        this.activateTool(this.volumeTool);
        this.volumeToolType = 'stockpile';
        openVolumeWindow(this);
    };

    activateModelVolumeTool = () => {
        console.log('activateModelVolumeTool');
        this.activateTool(this.volumeTool);
        this.volumeToolType = 'comparison';
        openVolumeWindow(this);
    };

    activateVolumeTool = () => {
        console.log('activateVolumeTool');
        this.activateTool(this.volumeTool);
        this.volumeToolType = 'stockpile';
        openVolumeWindow(this);
    };

    getDrawingsExtent = () => {
        const totalExtent = olExtent.createEmpty();

        try {
            olExtent.extend(totalExtent, this.areaTool.drawLayer.getSource().getExtent());
            olExtent.extend(totalExtent, this.distanceTool.drawLayer.getSource().getExtent());
            olExtent.extend(totalExtent, this.volumeTool.drawLayer.getSource().getExtent());
        } catch {
            console.warn('Failed to get drawings extent');
        }

        return totalExtent;
    };

    private onMeasurementsChange = () => {
        this.hasMeasurements = this.areaTool.getDrawingsCount() + this.distanceTool.getDrawingsCount() + this.volumeTool.getDrawingsCount() > 0;
    };

    clearMeasurementsBySource = (uuid: string) => {
        this.areaTool.clearBySource(uuid);
        this.distanceTool.clearBySource(uuid);
        this.volumeTool.clearBySource(uuid);
    };

    clearMeasurements = () => {
        this.areaTool.clear(false);
        this.distanceTool.clear(false);
        this.volumeTool.clearAll();
        this.hasMeasurements = false;
        Events.trigger(EventsGlobal.MAP_MEASUREMENTS_LOADED);
    };

    fitMeasurementBySource = (uuid: string) => {
        let extent = olExtent.createEmpty();
        olExtent.extend(extent, this.areaTool.getExtentBySource(uuid));
        olExtent.extend(extent, this.distanceTool.getExtentBySource(uuid));
        olExtent.extend(extent, this.volumeTool.getExtentBySource(uuid));
        if (olExtent.isEmpty(extent)) return;

        const pad = Math.floor(window.innerWidth * 0.1);
        this.mapWrapper.fitExtent(extent, 150, [pad, pad, pad, null]);
    };

    removeMeasurement = (feature: Feature) => {
        const type: FeatureType = feature?.get('type');
        if (!type) return;

        if (type === 'area-measurement') {
            this.areaTool.removeMeasurement(feature);
        } else if (type === 'volume-measurement') {
            this.volumeTool.removeMeasurement(feature);
        } else if (type === 'distance-measurement') {
            this.distanceTool.removeMeasurement(feature);
        }
    };

    // saved views only store pure measurement coords, no uuids
    // we need to match them with stored measurements so they can be un/check from the panel
    matchMeasurementsCoords = (mapMeasurements: IMapMeasurementsSavedCoords, sourceUuid: string) => {
        let matched = false;
        (mapMeasurements?.distance ?? []).forEach(measurement => {
            const match = this.findDistanceFeatureByCoords(measurement);
            if (match) {
                match.set('sourceUuid', sourceUuid);
                matched = true;
            }
        });
        (mapMeasurements?.area ?? []).forEach(measurement => {
            const match = this.findAreaFeatureByCoords(measurement);
            if (match) {
                match.set('sourceUuid', sourceUuid);
                matched = true;
            }
        });
        return matched;
    };

    restoreMeasurementsFromCoords = (mapMeasurements: IMapMeasurementsSavedCoords, clearExisting = true, fitExtent = false, sourceUuid?: string): Feature[] => {
        if (clearExisting) {
            this.clearMeasurements();
        }

        const features: Feature[] = [];
        if (mapMeasurements && (Array.isArray(mapMeasurements.distance) || Array.isArray(mapMeasurements.area))) {
            if (Array.isArray(mapMeasurements.distance)) {
                const measurements = mapMeasurements.distance
                    .filter(measurement => !this.findDistanceFeatureByCoords(measurement))
                    .map(measurement => {
                        return measurement.map(point => olTransform(point, 'EPSG:4326', 'EPSG:3857'));
                    });
                if (measurements.length > 0) {
                    this.activateDistanceTool();
                    features.push(...this.distanceTool.restoreFromCoords(measurements, sourceUuid));
                    this.deactivateAllTools();
                }
            }
            if (Array.isArray(mapMeasurements.area)) {
                const measurements = mapMeasurements.area
                    .filter(measurement => !this.findAreaFeatureByCoords(measurement))
                    .map(measurement => {
                        return measurement.map(outline => {
                            return outline.map(point => olTransform(point, 'EPSG:4326', 'EPSG:3857'));
                        });
                    });
                if (measurements.length > 0) {
                    this.activateAreaTool();
                    features.push(...this.areaTool.restoreFromCoords(measurements, sourceUuid));
                    this.deactivateAllTools();
                }
            }
            if (Array.isArray(mapMeasurements.volume)) {
                const measurements = mapMeasurements.volume.map(measurement => {
                    return measurement.map(outline => {
                        return outline.map(point => olTransform(point, 'EPSG:4326', 'EPSG:3857'));
                    });
                });
                if (measurements.length > 0) {
                    this.activateStockpileVolumeTool();
                    features.push(...this.volumeTool.restoreFromCoords(measurements, sourceUuid));
                    this.deactivateAllTools();
                }
            }
            if (fitExtent) {
                const pad = Math.floor(window.innerWidth * 0.1);
                this.mapWrapper.fitExtent(this.getDrawingsExtent(), 150, [pad, pad, pad, null]);
            }
            return features;
        } else {
            this.deactivateAllTools();
        }
    };

    findDistanceFeatureByCoords = (newMeasurement: number[][]): Feature => {
        const newStr = JSON.stringify(newMeasurement.map(p => p.map(c => c.toFixed(6))));
        const features = this.distanceTool.drawLayer.getSource().getFeatures();
        return features.find(f => {
            const existingCoords = (f.getGeometry() as any).getCoordinates();
            const existingStr = JSON.stringify(
                existingCoords.map((point: Coordinate) => olTransform(point, 'EPSG:3857', 'EPSG:4326')).map((p: number[]) => p.map(c => c.toFixed(6)))
            );
            return newStr === existingStr;
        });
    };

    findAreaFeatureByCoords = (newMeasurement: number[][][]): Feature => {
        const newStr = JSON.stringify(newMeasurement.map(outline => outline.map(p => p.map(c => c.toFixed(6)))));
        const features = this.areaTool.drawLayer.getSource().getFeatures();
        return features.find(f => {
            const existingCoords = (f.getGeometry() as any).getCoordinates();
            const existingStr = JSON.stringify(
                existingCoords.map((outline: Coordinate[]) =>
                    outline.map((point: Coordinate) => olTransform(point, 'EPSG:3857', 'EPSG:4326')).map((p: number[]) => p.map(c => c.toFixed(6)))
                )
            );
            return newStr === existingStr;
        });
    };

    convertModelToGeofence = async (modelId: number, n: number, e: number) => {
        const el = Drawer.open(380);
        el.classList.add('overlay-loading');
        const geofencesStore = new GeofencesStore();
        const materialsStore = new MaterialsStore(geofencesStore);
        await materialsStore.fetch();
        el.classList.remove('overlay-loading');

        const features = this.mapWrapper.featureLayers.models.getFeaturesBySource({ modelId }).filter(f => {
            const geom = f.getGeometry() as Polygon;
            return geom.intersectsCoordinate([e, n]);
        });

        if (features.length > 0) {
            const feature = features[0].clone();
            const layer = new VectorLayer({ source: new VectorSource({ features: [feature] }) });
            this.mapWrapper.addLayer(layer, 999, 'geofence-convert');
            GeofenceEditor.open(this.mapWrapper, geofencesStore, materialsStore, null, feature, () => {
                this.mapWrapper.removeLayer(layer);
                layer.dispose();
            });
        }
    };

    openLongSectionWindow = async (stationStart?: number, stationEnd?: number, floatingWindow?: IFloatingWindowStoredState) => {
        const activeAlignment = this.mapWrapper.featureLayers.alignments.getActiveAlignment();
        const includeLogpointIds = this.mapWrapper.featureLayers.logpoints.getVisibleFeatures().map(f => f.get('data').id as number);
        const includeModelIds = this.getSelectedModels();

        if (this.openCrossSectionInNewTab && !floatingWindow) {
            const url = [];
            url.push(kuura.getContextPath() + '/long_section.htm');
            url.push('?alignmentId=' + activeAlignment.modelId);
            url.push('&alignmentIndex=' + activeAlignment.index);
            if (includeModelIds) {
                url.push('&includeModelIds=' + includeModelIds.join(','));
            }
            if (stationStart) {
                url.push('&stationStart=' + stationStart);
            }
            if (stationEnd) {
                url.push('&stationEnd=' + stationEnd);
            }
            window.open(url.join(''));
        } else {
            const windowLoadSelection: ICrossSectionLoadSelection = floatingWindow?.data?.loadSelection;

            let bodyRef: LongSectionComponent = null;
            WindowManager.open({
                group: 'long-section',
                id: uuidv4(),
                title: __('js_map_measuring_tools.long_section'),
                allowMultiple: true,
                showCloseButton: true,
                preventCloseOthers: !!floatingWindow,
                disableAutoLayout: !!floatingWindow,
                resize: 'both',
                zIndex: floatingWindow?.zIndex ?? null,
                defaultSizing: floatingWindow?.sizing ?? {
                    height: window.innerHeight * 0.7,
                    width: window.innerWidth - 700,
                },
                getState: () => {
                    return bodyRef?.store?.getState();
                },
                body: (
                    <div className='flex-column' style={{ minWidth: 400, minHeight: 300 }}>
                        <LongSectionComponent
                            ref={el => (bodyRef = el)}
                            view={floatingWindow?.data?.view}
                            searchDisplacement={floatingWindow?.data?.searchDisplacement}
                            stationStart={floatingWindow?.data.stationStart ?? stationStart}
                            stationEnd={floatingWindow?.data.stationEnd ?? stationEnd}
                            selection={
                                windowLoadSelection ?? {
                                    alignmentId: activeAlignment?.modelId,
                                    alignmentIndex: activeAlignment?.index,
                                    includeModelIds,
                                    includeLogpointIds,
                                }
                            }
                        />
                    </div>
                ),
            });
        }
    };

    openCrossSectionWindow = async (
        station?: number,
        latLon1?: [number, number],
        latLon2?: [number, number],
        selectedLogpointId?: number,
        floatingWindow?: IFloatingWindowStoredState
    ) => {
        const activeAlignment = this.mapWrapper.featureLayers.alignments.getActiveAlignment();
        const includeLogpointIds = this.mapWrapper.featureLayers.logpoints.getVisibleFeatures().map(f => f.get('data').id as number);
        const includeModelIds = this.getSelectedModels();
        const includeDemUuids: string[] = this.getSelectedDemUuids();
        const includeLumiDbUuids: string[] = this.getSelectedLumiDbUuids();

        if (this.openCrossSectionInNewTab && !floatingWindow) {
            if (station) {
                window.open(getCrossSectionWindowUrl(station, activeAlignment.modelId, activeAlignment.index, selectedLogpointId, includeModelIds));
            } else {
                const url = [];
                url.push(kuura.getContextPath() + '/free_cross_section.htm');
                url.push('?projectId=' + kuura.getProjectId());
                url.push('&lat1=' + latLon1[0]);
                url.push('&lon1=' + latLon1[1]);
                url.push('&lat2=' + latLon2[0]);
                url.push('&lon2=' + latLon2[1]);

                if (activeAlignment?.modelId) {
                    url.push('&alignmentId=' + activeAlignment?.modelId);
                }
                if (activeAlignment?.index) {
                    url.push('&alignmentIndex=' + activeAlignment?.index);
                }
                if (selectedLogpointId) {
                    url.push('&selectedLogpointId=' + selectedLogpointId);
                }
                if (includeModelIds.length) {
                    url.push('&includeModelIds=' + includeModelIds.join(','));
                }
                window.open(url.join(''));
            }
        } else {
            let bodyRef: CrossSectionComponent = null;
            const windowLoadSelection: ICrossSectionLoadSelection = floatingWindow?.data?.loadSelection;
            console.log(windowLoadSelection);
            station = floatingWindow?.data?.station ?? station;
            const windowId = uuidv4();
            const windowFeatureId = 'cs-window-line-' + windowId;
            let isClosed = false;

            WindowManager.open({
                group: 'cross-section',
                id: windowId,
                title: __(latLon1 ? 'js_map_measuring_tools.free_cross_section' : 'js_map_measuring_tools.cross_section'),
                allowMultiple: true,
                showCloseButton: true,
                preventCloseOthers: !!floatingWindow,
                disableAutoLayout: !!floatingWindow,
                resize: 'both',
                zIndex: floatingWindow?.zIndex ?? null,
                defaultSizing: floatingWindow?.sizing ?? {
                    height: window.innerHeight * 0.7,
                    width: window.innerWidth - 700,
                },
                getState: () => {
                    return bodyRef?.store?.getState();
                },
                onClose: () => {
                    isClosed = true;
                    Events.trigger(MapStationEvents.CROSS_SECTION_WINDOW_LINE_REMOVE, { featureId: windowFeatureId });
                },
                body: (
                    <div className='flex-column' style={{ minWidth: 320, minHeight: 300 }}>
                        <CrossSectionComponent
                            ref={el => (bodyRef = el)}
                            station={station}
                            lat1={floatingWindow?.data?.lat1 ?? latLon1?.[0]}
                            lon1={floatingWindow?.data?.lon1 ?? latLon1?.[1]}
                            lat2={floatingWindow?.data?.lat2 ?? latLon2?.[0]}
                            lon2={floatingWindow?.data?.lon2 ?? latLon2?.[1]}
                            view={floatingWindow?.data?.view}
                            searchDisplacement={floatingWindow?.data?.searchDisplacement}
                            deltaZFilter={floatingWindow?.data?.deltaZFilter}
                            onFetch={data => {
                                if (data.station == null) return;
                                const csAlignments = data.alignment;
                                const activeAlignment = this.mapWrapper.featureLayers.alignments.getActiveAlignment();
                                if (csAlignments?.id === activeAlignment?.modelId && data.station?.toFixed(3) != station?.toFixed(3)) {
                                    this.panToStation(data.station);
                                }

                                const mapLine = this.getCrossSectionMapLine(data, latLon1, latLon2, floatingWindow);
                                if (mapLine) {
                                    Events.trigger(MapStationEvents.CROSS_SECTION_WINDOW_LINE_SET, { featureId: windowFeatureId, mapLine });
                                }
                            }}
                            selection={
                                windowLoadSelection ?? {
                                    alignmentId: activeAlignment?.modelId,
                                    alignmentIndex: activeAlignment?.index,
                                    selectedLogpointId: selectedLogpointId,
                                    includeModelIds,
                                    includeLumiDbUuids,
                                    includeDemUuids,
                                    includeLogpointIds,
                                }
                            }
                        />
                    </div>
                ),
            });

            const mapLine = this.getCrossSectionMapLine(null, latLon1, latLon2, floatingWindow);
            if (mapLine) {
                Events.trigger(MapStationEvents.CROSS_SECTION_WINDOW_LINE_SET, { featureId: windowFeatureId, mapLine });
            } else if (floatingWindow && station != null && windowLoadSelection?.alignmentId) {
                // legacy windows without saved map line
                this.restoreCrossSectionMapLineByStation(windowFeatureId, station, windowLoadSelection, () => isClosed);
            }

            if (latLon1 && latLon2 && !floatingWindow) {
                this.freeCrossSectionTool.clear();
            }
        }
    };

    private restoreCrossSectionMapLineByStation = async (featureId: string, station: number, loadSelection: ICrossSectionLoadSelection, isClosed: () => boolean): Promise<void> => {
        try {
            const stationCoordsResp = await Ajax2({
                url: kuura.getContextPath() + '/api/1/coords/stationCoordsByStation.json',
                params: {
                    projectId: kuura.getProjectId(),
                    station,
                    alignmentId: loadSelection.alignmentId,
                    alignmentIndex: loadSelection.alignmentIndex,
                },
            });

            const stationPoint = stationCoordsResp?.station;
            if (stationPoint?.lat == null || stationPoint?.lon == null) {
                return;
            }

            const stationLineResp = await MapCoordsService.fetchStationCoords(stationPoint.lat, stationPoint.lon, loadSelection.alignmentId, loadSelection.alignmentIndex);

            if (isClosed()) {
                return;
            }

            const stationLine = stationLineResp?.station;
            if (stationLine?.lat == null || stationLine?.lon == null || stationLine?.lat2 == null || stationLine?.lon2 == null) {
                return;
            }

            Events.trigger(MapStationEvents.CROSS_SECTION_WINDOW_LINE_SET, {
                featureId,
                mapLine: [
                    [stationLine.lat, stationLine.lon],
                    [stationLine.lat2, stationLine.lon2],
                ],
            });
        } catch (e) {
            console.warn('[CS] Failed to restore saved cross section map line by station', e);
        }
    };

    private getCrossSectionMapLine(
        data: ICrossSectionResponse | null,
        latLon1?: [number, number],
        latLon2?: [number, number],
        floatingWindow?: IFloatingWindowStoredState
    ): [[number, number], [number, number]] | null {
        const lat1 = floatingWindow?.data?.lat1 ?? latLon1?.[0];
        const lon1 = floatingWindow?.data?.lon1 ?? latLon1?.[1];
        const lat2 = floatingWindow?.data?.lat2 ?? latLon2?.[0];
        const lon2 = floatingWindow?.data?.lon2 ?? latLon2?.[1];

        if (lat1 != null && lon1 != null && lat2 != null && lon2 != null) {
            return [
                [lat1, lon1],
                [lat2, lon2],
            ];
        }

        if (data?.coordinates?.coords1 && data?.coordinates?.coords2) {
            const c1 = CoordsUtils.projectCoordinateToWgs84(data.coordinates.coords1);
            const c2 = CoordsUtils.projectCoordinateToWgs84(data.coordinates.coords2);
            return [
                [c2.lat, c2.lon],
                [c1.lat, c1.lon],
            ];
        }

        const mapLine = floatingWindow?.data?.mapLine;
        if (!mapLine) {
            return null;
        }

        if (floatingWindow?.data?.station != null) {
            return [mapLine[1], mapLine[0]];
        }

        return mapLine;
    }

    toggleShowSegmentLabels = () => {
        this.showSegmentLabels = !this.showSegmentLabels;
        LStorage.setItem('mapMeasurement-showSegmentLabels', this.showSegmentLabels);
        this.areaTool.showSegmentLabels = this.showSegmentLabels;
        this.distanceTool.showSegmentLabels = this.showSegmentLabels;
        this.areaTool.triggerChange();
        this.distanceTool.triggerChange();
        this.volumeTool.triggerChange();
    };

    panToStationAbort: AbortController = null;

    panToStation = _.debounce((station: number) => {
        if (this.panToStationAbort) {
            this.panToStationAbort.abort();
        }
        this.panToStationAbort = new AbortController();

        const activeAlignment = this.mapWrapper.featureLayers.alignments.getActiveAlignment();

        Ajax2({
            url: kuura.getContextPath() + '/api/1/coords/stationCoordsByStation.json',
            signal: this.panToStationAbort.signal,
            params: {
                projectId: kuura.getProjectId(),
                station,
                alignmentId: activeAlignment.modelId,
                alignmentIndex: activeAlignment.index,
            },
        })
            .then(
                (resp: {
                    station: {
                        lat: number;
                        lon: number;
                    };
                }) => {
                    if (resp.station.lat && resp.station.lat) {
                        const NE: Coordinate = olTransform([resp.station.lon, resp.station.lat], 'EPSG:4326', 'EPSG:3857');
                        this.mapWrapper.clickMarkerLayer.drawClickMarker(...(NE as [number, number]));
                        //this.mapWrapper.panTo(NE[0], NE[1]);
                    } else {
                    }
                }
            )
            .finally(() => {
                this.panToStationAbort = null;
            });
    }, 100);
}

function getCrossSectionWindowUrl(station: number, activeAlignmentId: number, activeAlignmentIndex: number, selectedLogpointId?: number, modelIds?: number[]) {
    if (!$.isNumeric(activeAlignmentId)) {
        console.log("No selected alignment. Can't draw cross section.");
        return null;
    }

    const url = [];
    url.push(kuura.getContextPath());
    url.push('/cross_section.htm');
    url.push('?alignmentId=' + activeAlignmentId);
    url.push('&alignmentIndex=' + activeAlignmentIndex);
    url.push('&includeModelIds=' + activeAlignmentId);
    if (modelIds?.length) {
        url.push(',');
        url.push(modelIds.join(','));
    }

    if (station) {
        url.push('&station=' + station);
    }

    if (selectedLogpointId) {
        url.push('&selectedLogpointId=');
        url.push(selectedLogpointId);
    }

    return url.join('');
}
